// src/hooks/useDeviceOrientation.js

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  canRequestDeviceOrientationPermission,
  hasDeviceOrientationEvent,
  isBrowser,
  isIOS,
} from "../utils/platform";
import { normalizeDegrees } from "../utils/geo";

/**
 * Device orientation / heading hook.
 *
 * iOS Safari requires a user gesture to request permission.
 * This hook exposes requestPermission() you can call from a button click.
 *
 * Returns:
 * - heading: number | null  (0..360, 0 = North)
 * - status: "idle" | "listening" | "denied" | "unsupported" | "error"
 * - error: string | null
 * - requestPermission: () => Promise<"granted" | "denied" | "unsupported">
 * - start: () => void
 * - stop: () => void
 */
export function useDeviceOrientation(options) {
  const { enabled = true, throttleMs = 50 } = options || {};

  const [heading, setHeading] = useState(null);
  const [status, setStatus] = useState(() => {
    if (!isBrowser()) return "unsupported";
    return hasDeviceOrientationEvent() ? "idle" : "unsupported";
  });
  const [error, setError] = useState(null);

  const listeningRef = useRef(false);
  const lastUpdateAtRef = useRef(0);

  const computeHeadingFromEvent = useCallback((event) => {
    // On iOS Safari, event.webkitCompassHeading is the best signal.
    // On many other browsers, event.alpha is compass-like but can be device-frame dependent.
    const anyEvent = event;

    if (anyEvent && typeof anyEvent.webkitCompassHeading === "number") {
      const h = anyEvent.webkitCompassHeading;
      if (Number.isFinite(h)) return normalizeDegrees(h);
    }

    // Fallback: use alpha if present
    if (event && typeof event.alpha === "number") {
      const a = event.alpha;
      if (Number.isFinite(a)) {
        // alpha is degrees clockwise from device facing north in many implementations
        return normalizeDegrees(a);
      }
    }

    return null;
  }, []);

  // Stable handler reference for add/removeEventListener.
  const onDeviceOrientation = useCallback(
    (event) => {
      const now = Date.now();
      const last = lastUpdateAtRef.current;
      const t = Math.max(0, Number(throttleMs) || 0);
      if (t > 0 && now - last < t) return;
      lastUpdateAtRef.current = now;

      const h = computeHeadingFromEvent(event);
      if (h === null) return;

      setHeading(h);
    },
    [computeHeadingFromEvent, throttleMs]
  );

  const stop = useCallback(() => {
    if (!isBrowser() || !hasDeviceOrientationEvent()) return;
    if (!listeningRef.current) return;

    try {
      window.removeEventListener("deviceorientation", onDeviceOrientation, true);
      window.removeEventListener(
        "deviceorientationabsolute",
        onDeviceOrientation,
        true
      );
    } catch {
      // ignore
    }

    listeningRef.current = false;
    setStatus((prev) => (prev === "unsupported" ? prev : "idle"));
  }, [onDeviceOrientation]);

  const start = useCallback(() => {
    if (!isBrowser() || !hasDeviceOrientationEvent()) {
      setStatus("unsupported");
      return;
    }
    if (listeningRef.current) return;

    setError(null);

    // On iOS, if permission is required and not granted, the events may be silent.
    // We still attach listeners, UI should call requestPermission() from a user gesture.
    try {
      window.addEventListener("deviceorientation", onDeviceOrientation, true);
      window.addEventListener(
        "deviceorientationabsolute",
        onDeviceOrientation,
        true
      );
      listeningRef.current = true;
      setStatus("listening");
    } catch (e) {
      setStatus("error");
      setError(e instanceof Error ? e.message : "Device orientation error");
    }
  }, [onDeviceOrientation]);

  const requestPermission = useCallback(async () => {
    if (!isBrowser() || !hasDeviceOrientationEvent()) return "unsupported";
    if (!canRequestDeviceOrientationPermission()) {
      // Non-iOS browsers typically do not need permission prompt here.
      start();
      return "granted";
    }

    // iOS Safari path
    try {
      const DOE = window.DeviceOrientationEvent;
      const result = await DOE.requestPermission();
      if (result === "granted") {
        // Ensure listeners are active immediately after permission grant.
        start();
        return "granted";
      }
      setStatus("denied");
      return "denied";
    } catch (e) {
      setStatus("error");
      setError(e instanceof Error ? e.message : "Permission request failed");
      return "denied";
    }
  }, [start]);

  // Auto start/stop when enabled changes
  useEffect(() => {
    if (!isBrowser() || !hasDeviceOrientationEvent()) {
      setStatus("unsupported");
      return undefined;
    }

    if (!enabled) {
      stop();
      return undefined;
    }

    start();

    return () => {
      stop();
    };
  }, [enabled, start, stop]);

  // iOS-specific hint: if permission is required and we have no heading after a moment,
  // UI should call requestPermission() from a click/tap.
  useEffect(() => {
    if (!isBrowser()) return undefined;
    if (!enabled) return undefined;
    if (!isIOS()) return undefined;
    if (!canRequestDeviceOrientationPermission()) return undefined;

    // Do not auto-prompt here (must be user gesture), just keep status informational.
    return undefined;
  }, [enabled]);

  const api = useMemo(
    () => ({
      heading,
      status,
      error,
      requestPermission,
      start,
      stop,
    }),
    [heading, status, error, requestPermission, start, stop]
  );

  return api;
}
